// src/hooks/useAgentRegistry.ts

import React from 'react';
import {
  toItems,
  useCreateAgentA2AConfigMutation,
  useCreateAgentCapabilityScopeMutation,
  useCreateAgentContextProviderMutation,
  useCreateAgentEndpointScopeMutation,
  useCreateAgentMutation,
  useCreateAgentPromptVersionMutation,
  useCreateAgentRoutingRuleMutation,
  useDeleteAgentCapabilityScopeMutation,
  useDeleteAgentContextProviderMutation,
  useDeleteAgentEndpointScopeMutation,
  useDeleteAgentMutation,
  useDeleteAgentPromptVersionMutation,
  useDeleteAgentRoutingRuleMutation,
  useFetchAgent,
  useFetchAgentA2AConfig,
  useFetchAgentA2AEndpoints,
  useFetchAgentCapabilityScopes,
  useFetchAgentContextProviders,
  useFetchAgentEndpointScopes,
  useFetchAgentInvocationAudits,
  useFetchAgentPromptVersions,
  useFetchAgentRoutingRules,
  useFetchAgents,
  useFetchDocumentationEntityTypes,
  useUpdateAgentA2AConfigMutation,
  useUpdateAgentCapabilityScopeMutation,
  useUpdateAgentContextProviderMutation,
  useUpdateAgentEndpointScopeMutation,
  useUpdateAgentMutation,
  useUpdateAgentPromptVersionMutation,
  useUpdateAgentRoutingRuleMutation,
} from './ai-hooks';
import { ID } from '../types/aiTypes';

function unwrapAgent(resp: any) {
  if (!resp) return null;
  if (resp?.id) return resp;
  if (resp?.data?.id) return resp.data;
  return resp;
}

/**
 * Sync a nested child collection against what currently exists in the DB.
 *
 * Mirrors `useMcpRegistry.saveServer`: for each incoming row, PUT-update when it
 * carries an id, otherwise create it; then delete any previously-existing row
 * whose id is no longer present. `decorate` injects the parent FK (and any
 * other derived fields) onto every payload before it is sent.
 */
async function syncChildren(opts: {
  existing: any[];
  incoming: any[];
  decorate: (row: any) => any;
  create: (payload: any) => Promise<any>;
  update: (payload: any) => Promise<any>;
  remove: (id: ID) => Promise<any>;
}) {
  const { existing, incoming, decorate, create, update, remove } = opts;

  const existingById = new Map<string, any>(
    existing
      .filter((row) => row?.id)
      .map((row) => [String(row.id), row] as [string, any])
  );

  const incomingIds = new Set<string>();

  for (const row of incoming) {
    const payload = decorate(row);

    if (row?.id) {
      incomingIds.add(String(row.id));
      await update(payload);
    } else {
      await create(payload);
    }
  }

  for (const [id] of existingById) {
    if (!incomingIds.has(id)) {
      await remove(id);
    }
  }
}

export function useAgentRegistry() {
  const [selectedAgentId, setSelectedAgentId] = React.useState<ID | null>(null);

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editorInitialValue, setEditorInitialValue] =
    React.useState<any | null>(null);

  const {
    data: agentsResp,
    refetch: refetchAgents,
    isFetching: agentsLoading,
  } = useFetchAgents();

  const baseAgents = React.useMemo<any[]>(
    () => toItems(agentsResp),
    [agentsResp]
  );

  const selectedListAgent = React.useMemo(() => {
    if (!selectedAgentId) return null;
    return (
      baseAgents.find(
        (agent: any) => String(agent.id) === String(selectedAgentId)
      ) || null
    );
  }, [baseAgents, selectedAgentId]);

  const enabled = { enabled: !!selectedAgentId };

  const { data: agentResp, refetch: refetchAgent } = useFetchAgent(
    selectedAgentId,
    enabled
  );

  const { data: promptsResp, refetch: refetchPrompts } =
    useFetchAgentPromptVersions(selectedAgentId, enabled);

  const { data: scopesResp, refetch: refetchScopes } =
    useFetchAgentCapabilityScopes(selectedAgentId, enabled);

  const { data: providersResp, refetch: refetchProviders } =
    useFetchAgentContextProviders(selectedAgentId, enabled);

  const { data: rulesResp, refetch: refetchRules } = useFetchAgentRoutingRules(
    selectedAgentId,
    enabled
  );

  const { data: a2aConfigResp, refetch: refetchA2AConfig } =
    useFetchAgentA2AConfig(selectedAgentId, enabled);

  const { data: endpointScopesResp, refetch: refetchEndpointScopes } =
    useFetchAgentEndpointScopes(selectedAgentId, enabled);

  const { data: auditsResp, refetch: refetchAudits } =
    useFetchAgentInvocationAudits(selectedAgentId, enabled);

  const { data: endpointsResp, refetch: refetchEndpoints } =
    useFetchAgentA2AEndpoints();

  const { data: entityTypesResp } = useFetchDocumentationEntityTypes();

  const rawAgent = React.useMemo(() => unwrapAgent(agentResp), [agentResp]);

  // Prefer the FK-filtered fetch; fall back to the nested arrays the
  // `?detailed=true` parent record already carries (mirrors useMcpRegistry).
  const pickNested = React.useCallback(
    (direct: any[], key: string) => {
      if (direct.length) return direct;
      if (Array.isArray(rawAgent?.[key]) && rawAgent[key].length) {
        return rawAgent[key];
      }
      if (
        Array.isArray(selectedListAgent?.[key]) &&
        selectedListAgent[key].length
      ) {
        return selectedListAgent[key];
      }
      return [];
    },
    [rawAgent, selectedListAgent]
  );

  const promptVersions = React.useMemo<any[]>(
    () => pickNested(toItems(promptsResp), 'prompt_versions'),
    [promptsResp, pickNested]
  );

  const capabilityScopes = React.useMemo<any[]>(
    () => pickNested(toItems(scopesResp), 'capability_scopes'),
    [scopesResp, pickNested]
  );

  const contextProviders = React.useMemo<any[]>(
    () => pickNested(toItems(providersResp), 'context_providers'),
    [providersResp, pickNested]
  );

  const routingRules = React.useMemo<any[]>(
    () => pickNested(toItems(rulesResp), 'outgoing_rules'),
    [rulesResp, pickNested]
  );

  const endpointScopes = React.useMemo<any[]>(
    () => pickNested(toItems(endpointScopesResp), 'endpoint_scopes'),
    [endpointScopesResp, pickNested]
  );

  const a2aConfig = React.useMemo<any | null>(() => {
    const direct = toItems(a2aConfigResp);
    if (direct.length) return direct[0];
    if (rawAgent?.a2a_config) return rawAgent.a2a_config;
    if (selectedListAgent?.a2a_config) return selectedListAgent.a2a_config;
    return null;
  }, [a2aConfigResp, rawAgent, selectedListAgent]);

  const audits = React.useMemo<any[]>(
    () => toItems(auditsResp),
    [auditsResp]
  );

  const endpoints = React.useMemo<any[]>(
    () => toItems(endpointsResp),
    [endpointsResp]
  );

  const entityTypes = React.useMemo<any[]>(
    () => toItems(entityTypesResp),
    [entityTypesResp]
  );

  const agent = React.useMemo(() => {
    const merged = {
      ...(selectedListAgent || {}),
      ...(rawAgent || {}),
    };

    if (!merged?.id && !selectedListAgent?.id && !rawAgent?.id) {
      return null;
    }

    return {
      ...merged,
      prompt_versions: promptVersions,
      capability_scopes: capabilityScopes,
      context_providers: contextProviders,
      outgoing_rules: routingRules,
      endpoint_scopes: endpointScopes,
      a2a_config: a2aConfig,
    };
  }, [
    a2aConfig,
    capabilityScopes,
    contextProviders,
    endpointScopes,
    promptVersions,
    rawAgent,
    routingRules,
    selectedListAgent,
  ]);

  // Mutations
  const createAgent = useCreateAgentMutation();
  const updateAgent = useUpdateAgentMutation();
  const deleteAgent = useDeleteAgentMutation();

  const createPrompt = useCreateAgentPromptVersionMutation();
  const updatePrompt = useUpdateAgentPromptVersionMutation();
  const deletePrompt = useDeleteAgentPromptVersionMutation();

  const createScope = useCreateAgentCapabilityScopeMutation();
  const updateScope = useUpdateAgentCapabilityScopeMutation();
  const deleteScope = useDeleteAgentCapabilityScopeMutation();

  const createProvider = useCreateAgentContextProviderMutation();
  const updateProvider = useUpdateAgentContextProviderMutation();
  const deleteProvider = useDeleteAgentContextProviderMutation();

  const createRule = useCreateAgentRoutingRuleMutation();
  const updateRule = useUpdateAgentRoutingRuleMutation();
  const deleteRule = useDeleteAgentRoutingRuleMutation();

  const createA2AConfig = useCreateAgentA2AConfigMutation();
  const updateA2AConfig = useUpdateAgentA2AConfigMutation();

  const createEndpointScope = useCreateAgentEndpointScopeMutation();
  const updateEndpointScope = useUpdateAgentEndpointScopeMutation();
  const deleteEndpointScope = useDeleteAgentEndpointScopeMutation();

  const refreshAll = React.useCallback(async () => {
    await refetchAgents();
    await refetchEndpoints();

    if (selectedAgentId) {
      await Promise.all([
        refetchAgent(),
        refetchPrompts(),
        refetchScopes(),
        refetchProviders(),
        refetchRules(),
        refetchA2AConfig(),
        refetchEndpointScopes(),
        refetchAudits(),
      ]);
    }
  }, [
    refetchA2AConfig,
    refetchAgent,
    refetchAgents,
    refetchAudits,
    refetchEndpointScopes,
    refetchEndpoints,
    refetchProviders,
    refetchPrompts,
    refetchRules,
    refetchScopes,
    selectedAgentId,
  ]);

  const openCreate = React.useCallback(() => {
    setEditorInitialValue(null);
    setEditorOpen(true);
  }, []);

  const openEdit = React.useCallback(
    (row: any) => {
      const isSelected = String(selectedAgentId) === String(row.id);

      const hydrated = {
        ...row,
        ...(agent && String(agent.id) === String(row.id) ? agent : {}),
        prompt_versions: isSelected ? promptVersions : row.prompt_versions || [],
        capability_scopes: isSelected
          ? capabilityScopes
          : row.capability_scopes || [],
        context_providers: isSelected
          ? contextProviders
          : row.context_providers || [],
        outgoing_rules: isSelected ? routingRules : row.outgoing_rules || [],
        endpoint_scopes: isSelected ? endpointScopes : row.endpoint_scopes || [],
        a2a_config: isSelected ? a2aConfig : row.a2a_config || null,
      };

      setEditorInitialValue(hydrated);
      setEditorOpen(true);
    },
    [
      a2aConfig,
      agent,
      capabilityScopes,
      contextProviders,
      endpointScopes,
      promptVersions,
      routingRules,
      selectedAgentId,
    ]
  );

  const closeEditor = React.useCallback(() => {
    setEditorOpen(false);
    setEditorInitialValue(null);
  }, []);

  const saveAgent = React.useCallback(
    async (payload: any) => {
      const {
        prompt_versions = [],
        capability_scopes = [],
        context_providers = [],
        outgoing_rules = [],
        endpoint_scopes = [],
        a2a_config = null,
        ...agentPayload
      } = payload || {};

      let agentId: ID | null = agentPayload.id || null;

      if (agentId) {
        await updateAgent.mutateAsync(agentPayload);
      } else {
        const created: any = await createAgent.mutateAsync(agentPayload);
        agentId = created?.id || created?.data?.id || null;
      }

      if (!agentId) {
        closeEditor();
        await refetchAgents();
        return;
      }

      await syncChildren({
        existing: promptVersions,
        incoming: prompt_versions,
        decorate: (row) => ({ ...row, agent: agentId }),
        create: (p) => createPrompt.mutateAsync(p),
        update: (p) => updatePrompt.mutateAsync(p),
        remove: (id) => deletePrompt.mutateAsync(id),
      });

      await syncChildren({
        existing: capabilityScopes,
        incoming: capability_scopes,
        decorate: (row) => ({ ...row, agent: agentId }),
        create: (p) => createScope.mutateAsync(p),
        update: (p) => updateScope.mutateAsync(p),
        remove: (id) => deleteScope.mutateAsync(id),
      });

      await syncChildren({
        existing: contextProviders,
        incoming: context_providers,
        decorate: (row) => ({ ...row, agent: agentId }),
        create: (p) => createProvider.mutateAsync(p),
        update: (p) => updateProvider.mutateAsync(p),
        remove: (id) => deleteProvider.mutateAsync(id),
      });

      await syncChildren({
        existing: routingRules,
        incoming: outgoing_rules,
        decorate: (row) => ({ ...row, orchestrator: agentId }),
        create: (p) => createRule.mutateAsync(p),
        update: (p) => updateRule.mutateAsync(p),
        remove: (id) => deleteRule.mutateAsync(id),
      });

      await syncChildren({
        existing: endpointScopes,
        incoming: endpoint_scopes,
        decorate: (row) => ({ ...row, agent: agentId }),
        create: (p) => createEndpointScope.mutateAsync(p),
        update: (p) => updateEndpointScope.mutateAsync(p),
        remove: (id) => deleteEndpointScope.mutateAsync(id),
      });

      // A2A config is a single record (one per agent): create or update.
      if (a2a_config) {
        const configPayload = { ...a2a_config, agent: agentId };
        const existingConfigId = a2aConfig?.id;

        if (existingConfigId) {
          await updateA2AConfig.mutateAsync({
            ...configPayload,
            id: existingConfigId,
          });
        } else if (
          a2a_config.auth_mode ||
          a2a_config.inbound_enabled ||
          a2a_config.outbound_enabled
        ) {
          await createA2AConfig.mutateAsync(configPayload);
        }
      }

      setSelectedAgentId(agentId);
      closeEditor();
      await refreshAll();
    },
    [
      a2aConfig,
      capabilityScopes,
      closeEditor,
      contextProviders,
      createA2AConfig,
      createAgent,
      createEndpointScope,
      createProvider,
      createPrompt,
      createRule,
      createScope,
      deleteEndpointScope,
      deleteProvider,
      deletePrompt,
      deleteRule,
      deleteScope,
      endpointScopes,
      promptVersions,
      refetchAgents,
      refreshAll,
      routingRules,
      updateA2AConfig,
      updateAgent,
      updateEndpointScope,
      updateProvider,
      updatePrompt,
      updateRule,
      updateScope,
    ]
  );

  const removeAgent = React.useCallback(
    async (agentId: ID) => {
      if (!window.confirm('Delete this agent and all of its configuration?'))
        return;

      await deleteAgent.mutateAsync(agentId);

      if (String(selectedAgentId) === String(agentId)) {
        setSelectedAgentId(null);
      }

      await refetchAgents();
    },
    [deleteAgent, refetchAgents, selectedAgentId]
  );

  const selectAgent = React.useCallback((agentId: ID) => {
    setSelectedAgentId(agentId);
  }, []);

  return {
    agents: baseAgents,
    agentsLoading,
    selectedAgentId,
    selectAgent,

    agent,
    promptVersions,
    capabilityScopes,
    contextProviders,
    routingRules,
    endpointScopes,
    a2aConfig,
    audits,

    endpoints,
    entityTypes,

    editorOpen,
    editorInitialValue,
    openCreate,
    openEdit,
    closeEditor,
    saveAgent,

    removeAgent,
    refreshAll,

    saving:
      createAgent.isPending ||
      updateAgent.isPending ||
      createPrompt.isPending ||
      updatePrompt.isPending ||
      createScope.isPending ||
      updateScope.isPending ||
      createProvider.isPending ||
      updateProvider.isPending ||
      createRule.isPending ||
      updateRule.isPending ||
      createA2AConfig.isPending ||
      updateA2AConfig.isPending ||
      createEndpointScope.isPending ||
      updateEndpointScope.isPending,
  };
}
